import React, {useState, useEffect, useCallback, useMemo} from 'react';
import styled from 'styled-components';
import Helmet from 'react-helmet';
import {useStore} from 'react-hookstore';
import fetcher from '../modules/fetcher';
import notifications from '../modules/notifications';
import NoData from '../assets/no_data.svg';
import {colors, media, radius} from '../modules/styles';
import useAddToReport from '../hooks/useAddToReport';
import PolicyContents from '../components/PolicyContents';
import PoliciesFilters from '../components/PoliciesFilters';

export default function PoliciesPg() {
  const [isLoading, setIsLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [reportData, setReportData] = useState([]);

  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState([]);

  const [currentUser] = useStore('currentUser');
  const {createReport, appendToReport} = useAddToReport();

  const filteredData = useMemo(
    () =>
      reportData.filter(
        prov =>
          (selectedAreas.length === 0 ||
            selectedAreas.includes(prov.area.id)) &&
          (selectedJurisdictions.length === 0 ||
            selectedJurisdictions.includes(prov.jurisdiction))
      ),
    [reportData, selectedAreas, selectedJurisdictions]
  );

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetcher.get('/api/buildReportViaPolicies');
      setReportData(res.data);
      setIsLoading(false);
    } catch (e) {
      notifications.error(`Error building report: ${e.message}`);
      setReportData([]);
    }
  }, []);

  // First time load... Populate the pulldowns
  useEffect(() => {
    const loadOptionsData = async () => {
      try {
        const areasRes = await fetcher.get('/api/areas/withPrunedTopics');
        setAreas(areasRes.data);
        await loadData([]);
      } catch (e) {
        notifications.error(`Error loading menu data: ${e.message}`);
      }
    };

    loadOptionsData();
  }, [loadData]);

  const handleCreateReport = async name => {
    createReport(
      name,
      filteredData.map(p => p.id)
    );
  };

  const handleAppendToReport = async reportId => {
    appendToReport(
      reportId,
      filteredData.map(p => p.id)
    );
  };

  return (
    <Comp>
      <Helmet title="Policies" />

      <FilterCol>
        <Filters>
          <Label>What are Policies?</Label>

          <p>
            "Policies" provide sample policy language for <strong>legally</strong> required policies. Sample policies are not legal advice. Contact a lawyer for
            advice for your particular set of facts or circumstances. Sample policies may not be suitable for all purposes - be sure to customize the policy to
            your workplace.
          </p>
          <br />

          <PoliciesFilters
            currentUser={currentUser}
            areas={areas}
            selectedAreas={selectedAreas}
            selectedJurisdictions={selectedJurisdictions}
            setSelectedAreas={setSelectedAreas}
            setSelectedJurisdictions={setSelectedJurisdictions}
            onCreateReport={handleCreateReport}
            onAppendToReport={handleAppendToReport}
            results={reportData}
          />
        </Filters>
      </FilterCol>

      <Content>
        <h1>Policies</h1>

        {!isLoading && filteredData.length > 0 && (
          <Box>
            <PolicyContents
              provisions={filteredData}
              showAreaName={false}
              isRemoveEnabled={false}
              onCreateReport={createReport}
              onAppendToReport={appendToReport}
            />
          </Box>
        )}

        {filteredData.length === 0 && !isLoading && (
          <NoReports>
            <img src={NoData} alt="No results found" />

            <h2>No results found.</h2>
            <p>Please adjust your filters and try again.</p>
          </NoReports>
        )}
      </Content>
    </Comp>
  );
}

const Comp = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media (min-width: ${media.lg}) {
    flex-direction: row;
    padding: 0px;
  }
`;

const Content = styled.section`
  margin: 20px 0 0 0;
  width: 100%;

  @media (min-width: ${media.lg}) {
    margin: 20px 46px 0 330px;
  }

  @media (min-width: ${media.xl}) {
    margin: 20px 250px 0 330px;
  }
`;

const Box = styled.div`
  border: 1px solid ${colors.gray2};
  background-color: white;
  border-radius: ${radius};
  padding: 5px 10px;
  width: 100%;
  max-width: 1100px;
  margin-bottom: 30px;

  @media (min-width: ${media.lg}) {
    padding: 15px 30px;
    position: relative;
  }
`;

const FilterCol = styled.div`
  width: 100%;

  @media (min-width: ${media.lg}) {
    width: 260px;
    min-width: 260px;
    position: fixed;
    width: 220px;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media (min-width: ${media.lg}) {
    min-width: 227px;
    max-width: 227px;
    margin: 92px 0px 0 50px;
    padding: 0;
  }
`;

const Label = styled.h2`
  font-size: 1.2rem;
  margin: 0 0 20px 0;
`;

const NoReports = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 50px;
  width: 100%;

  img {
    width: 40%;
  }

  h2 {
    font-size: 1.1rem;
    text-align: center;
    line-height: 1.8rem;
  }

  a {
    color: ${colors.primary};
    text-decoration: underline;
  }
`;
